// src/components/WorkflowCreator/nodes/CustomNodes.jsx
// Custom node components for ReactFlow workflow editor

import React, { memo } from 'react';
import { Handle, Position } from 'reactflow';
import { motion } from 'framer-motion';
import {
  Zap, Bot, ShoppingBag, MessageCircle, Globe, Database,
  GitBranch, Timer, Clock, Mail, Hash, MessageSquare,
  Send, Shuffle, FileText, AlertTriangle, Hourglass,
  Sparkles, Webhook, Settings, Plug, Bell, CheckCircle,
  AlertCircle
} from 'lucide-react';

// Map icon names to components
const ICON_MAP = {
  Zap, Bot, ShoppingBag, MessageCircle, Globe, Database,
  GitBranch, Timer, Clock, Mail, Hash, MessageSquare,
  Send, Shuffle, FileText, AlertTriangle, Hourglass,
  Sparkles, Webhook, Settings, Plug, Bell, CheckCircle,
  AlertCircle
};

const getIcon = (iconName) => ICON_MAP[iconName] || Zap;

// Base styles for handles
const handleBaseStyle = {
  width: 12,
  height: 12,
  border: '2px solid #6366f1',
  background: '#1e1b4b',
};

/**
 * Trigger Node - Starting point of workflows
 */
export const TriggerNode = memo(({ data, selected }) => {
  const Icon = getIcon(data.icon);
  const isConfigured = data.isConfigured;

  return (
    <motion.div
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      whileHover={{ scale: 1.02 }}
      className={`
        relative w-[240px]
        rounded-xl overflow-hidden
        ${selected ? 'ring-2 ring-green-400 ring-offset-2 ring-offset-[#0d0d0d]' : ''}
      `}
    >
      {/* Gradient background */}
      <div className={`absolute inset-0 bg-gradient-to-br ${data.color || 'from-green-500 to-emerald-600'} opacity-20`} />
      
      {/* Glass effect */}
      <div className="relative bg-[#1a1a2e]/90 backdrop-blur-xl border border-white/10 rounded-xl p-4">
        {/* Status indicator */}
        <div className={`absolute top-2 right-2 w-2 h-2 rounded-full ${isConfigured ? 'bg-green-400' : 'bg-yellow-400'} animate-pulse`} />
        
        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${data.color || 'from-green-500 to-emerald-600'} flex items-center justify-center shadow-lg`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div>
            <span className="text-[10px] font-semibold text-green-400 uppercase tracking-wider">Trigger</span>
            <h3 className="text-white font-semibold text-sm">{data.label}</h3>
          </div>
        </div>

        {/* Description */}
        <p className="text-gray-200 text-xs line-clamp-2">{data.description}</p>

        {/* Configuration preview */}
        {data.params && Object.keys(data.params).length > 0 && (
          <div className="mt-2 pt-2 border-t border-white/5">
            <span className="text-[10px] text-gray-300">
              {Object.keys(data.params).length} parameter(s) configured
            </span>
          </div>
        )}
      </div>

      {/* Handles - 1 por lado */}
      <Handle
        type="source"
        position={Position.Top}
        id="top"
        style={{
          ...handleBaseStyle,
          top: -6,
          background: '#22c55e',
          borderColor: '#16a34a',
        }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="right"
        style={{
          ...handleBaseStyle,
          right: -6,
          background: '#22c55e',
          borderColor: '#16a34a',
        }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom"
        style={{
          ...handleBaseStyle,
          bottom: -6,
          background: '#22c55e',
          borderColor: '#16a34a',
        }}
      />
      <Handle
        type="source"
        position={Position.Left}
        id="left"
        style={{
          ...handleBaseStyle,
          left: -6,
          background: '#22c55e',
          borderColor: '#16a34a',
        }}
      />
    </motion.div>
  );
});

/**
 * Action Node - Standard workflow step
 */
export const ActionNode = memo(({ data, selected }) => {
  const Icon = getIcon(data.icon);
  const isConfigured = data.isConfigured;

  return (
    <motion.div
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      whileHover={{ scale: 1.02 }}
      className={`
        relative w-[240px]
        rounded-xl overflow-hidden
        ${selected ? 'ring-2 ring-purple-400 ring-offset-2 ring-offset-[#0d0d0d]' : ''}
      `}
    >
      {/* Gradient background */}
      <div className={`absolute inset-0 bg-gradient-to-br ${data.color || 'from-purple-500 to-violet-600'} opacity-20`} />
      
      {/* Glass effect */}
      <div className="relative bg-[#1a1a2e]/90 backdrop-blur-xl border border-white/10 rounded-xl p-4">
        {/* Status indicator */}
        <div className={`absolute top-2 right-2 w-2 h-2 rounded-full ${isConfigured ? 'bg-green-400' : 'bg-yellow-400'}`} />
        
        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${data.color || 'from-purple-500 to-violet-600'} flex items-center justify-center shadow-lg`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <span className="text-[10px] font-semibold text-purple-400 uppercase tracking-wider">
              {data.category || 'Action'}
            </span>
            <h3 className="text-white font-semibold text-sm truncate">{data.label}</h3>
          </div>
        </div>

        {/* Description */}
        <p className="text-gray-200 text-xs line-clamp-2">{data.description}</p>

        {/* Step name if set */}
        {data.stepName && (
          <div className="mt-2 pt-2 border-t border-white/5">
            <span className="text-[10px] text-indigo-400 font-medium">
              "{data.stepName}"
            </span>
          </div>
        )}
      </div>

      {/* Handles - 1 por lado, todos bidireccionales */}
      <Handle
        type="target"
        position={Position.Top}
        id="top"
        style={{
          ...handleBaseStyle,
          top: -6,
        }}
        isConnectable
      />
      <Handle
        type="source"
        position={Position.Right}
        id="right"
        style={{
          ...handleBaseStyle,
          right: -6,
        }}
        isConnectable
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom"
        style={{
          ...handleBaseStyle,
          bottom: -6,
        }}
        isConnectable
      />
      <Handle
        type="target"
        position={Position.Left}
        id="left"
        style={{
          ...handleBaseStyle,
          left: -6,
        }}
        isConnectable
      />
    </motion.div>
  );
});

/**
 * Condition Node - Branching logic
 */
export const ConditionNode = memo(({ data, selected }) => {
  const Icon = getIcon('GitBranch');
  const isConfigured = data.isConfigured;
  
  // Obtener condiciones para preview
  const conditions = data.params?.conditions || [];
  const logic = data.params?.logic || 'AND';
  const hasConditions = conditions.length > 0;

  return (
    <motion.div
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      whileHover={{ scale: 1.02 }}
      className={`
        relative w-[240px]
        ${selected ? 'ring-2 ring-pink-400 ring-offset-2 ring-offset-[#0d0d0d]' : ''}
      `}
    >
      {/* Diamond shape container */}
      <div className="relative">
        {/* Gradient background - diamond shape */}
        <div className="absolute inset-0 bg-gradient-to-br from-pink-500 to-rose-600 opacity-20 transform rotate-45 scale-75 rounded-lg" />
        
        {/* Glass effect */}
        <div className="relative bg-[#1a1a2e]/90 backdrop-blur-xl border border-white/10 rounded-xl p-4">
          {/* Status indicator */}
          <div className={`absolute top-2 right-2 w-2 h-2 rounded-full ${isConfigured ? 'bg-green-400' : 'bg-yellow-400'}`} />
          
          {/* Header */}
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center shadow-lg transform rotate-0">
              <Icon className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="text-[10px] font-semibold text-pink-400 uppercase tracking-wider">Condition</span>
              <h3 className="text-white font-semibold text-sm">If / Else</h3>
            </div>
          </div>

          {/* Conditions preview */}
          {hasConditions ? (
            <div className="space-y-1">
              <div className="bg-black/30 px-2 py-1 rounded">
                {conditions.slice(0, 2).map((cond, idx) => (
                  <p key={idx} className="text-gray-200 text-xs font-mono truncate">
                    {cond.variable?.substring(0, 15)}... {cond.operator || '=='} {cond.value?.substring(0, 10)}
                  </p>
                ))}
                {conditions.length > 2 && (
                  <p className="text-gray-400 text-xs">+{conditions.length - 2} more</p>
                )}
              </div>
              {conditions.length > 1 && (
                <div className="text-center">
                  <span className="text-[10px] text-purple-400 font-semibold">{logic}</span>
                </div>
              )}
            </div>
          ) : (
            <p className="text-gray-500 text-xs italic">No conditions set</p>
          )}

          {/* Branch labels */}
          <div className="flex justify-between mt-2 text-[10px]">
            <span className="text-green-400">✓ True</span>
            <span className="text-red-400">✗ False</span>
          </div>
        </div>
      </div>

      {/* Handles */}
      <Handle
        type="target"
        position={Position.Top}
        id="top"
        style={{
          ...handleBaseStyle,
          top: -6,
        }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="true"
        style={{
          ...handleBaseStyle,
          right: -6,
          top: '30%',
          background: '#22c55e',
          borderColor: '#16a34a',
        }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="false"
        style={{
          ...handleBaseStyle,
          right: -6,
          top: '70%',
          background: '#ef4444',
          borderColor: '#dc2626',
        }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom"
        style={{
          ...handleBaseStyle,
          bottom: -6,
          background: '#22c55e',
          borderColor: '#16a34a',
        }}
      />
      <Handle
        type="target"
        position={Position.Left}
        id="left"
        style={{
          ...handleBaseStyle,
          left: -6,
        }}
      />
    </motion.div>
  );
});

/**
 * Delay Node - Wait step
 */
export const DelayNode = memo(({ data, selected }) => {
  const Icon = getIcon('Timer');
  const duration = data.params?.duration_seconds || 60;

  const formatDuration = (seconds) => {
    if (seconds >= 3600) return `${Math.floor(seconds / 3600)}h`;
    if (seconds >= 60) return `${Math.floor(seconds / 60)}m`;
    return `${seconds}s`;
  };

  return (
    <motion.div
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      whileHover={{ scale: 1.02 }}
      className={`
        relative w-[240px]
        rounded-xl overflow-hidden
        ${selected ? 'ring-2 ring-amber-400 ring-offset-2 ring-offset-[#0d0d0d]' : ''}
      `}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-amber-500 to-orange-600 opacity-20" />
      
      <div className="relative bg-[#1a1a2e]/90 backdrop-blur-xl border border-white/10 rounded-xl p-3 text-center">
        <div className="w-12 h-12 mx-auto rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg mb-2">
          <Icon className="w-6 h-6 text-white" />
        </div>
        <span className="text-[10px] font-semibold text-amber-400 uppercase tracking-wider">Delay</span>
        <h3 className="text-white font-bold text-lg">{formatDuration(duration)}</h3>
      </div>

      {/* Handles - 1 por lado */}
      <Handle
        type="target"
        position={Position.Top}
        id="top"
        style={{ ...handleBaseStyle, top: -6 }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="right"
        style={{ ...handleBaseStyle, right: -6 }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="bottom"
        style={{ ...handleBaseStyle, bottom: -6 }}
      />
      <Handle
        type="target"
        position={Position.Left}
        id="left"
        style={{ ...handleBaseStyle, left: -6 }}
      />
    </motion.div>
  );
});

/**
 * Node types registry for ReactFlow
 */
export const nodeTypes = {
  // Triggers
  shopify_trigger: TriggerNode,
  webhook_trigger: TriggerNode,
  schedule_trigger: TriggerNode,
  manual_trigger: TriggerNode,
  whatsapp_trigger: TriggerNode,
  
  // AI
  ai_assistant: ActionNode,
  assistant_task: ActionNode,
  llm_call: ActionNode,
  
  // Integrations
  shopify_action: ActionNode,
  shopify_update_order: ActionNode,
  api_call: ActionNode,
  http_request: ActionNode,
  webhook: ActionNode,
  
  // Notifications
  send_email: ActionNode,
  send_slack: ActionNode,
  send_whatsapp: ActionNode,
  whatsapp_notification: ActionNode,
  slack_notification: ActionNode,
  discord_notification: ActionNode,
  gmail: ActionNode,
  
  // Data
  transform_data: ActionNode,
  data_transform: ActionNode,
  database_query: ActionNode,
  
  // Control
  condition: ConditionNode,
  delay: DelayNode,
  wait: ActionNode,
  error_handler: ActionNode,
  
  // System
  log: ActionNode,
  
  // Fallback - generic workflow node
  workflowNode: ActionNode,
};

export default nodeTypes;
